import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { RotateCw } from 'lucide-react'
import { useOrganization } from '../contexts/OrganizationContext'
import {
  Button,
  Field,
  Input,
  LinkButton,
  PageHeader,
  PageLayout,
  PageToolbar,
  SegmentedControl,
  Select,
  StatePanel,
} from '../components/common'
import { kambiApi, surfacesApi } from '../services/api'
import { getFeedPreviewApiUrl } from './feedPreviewUrl'
import { labelForDisplayType } from '../constants/displayTypes'
import { WIDGET_BRANDS } from '../constants/widgetBrands'
import { STUDIO_BUILD, describeStudioBuild } from '../config/buildInfo'
import { describePublishedRelease, describeReleaseDrift, fetchLatestWidgetRelease } from '../services/widgetReleases'
import {
  RELEASE_ID_PATTERN,
  buildPreviewHostUrl,
  hostActivityNotice,
  parsePreviewHostMessage,
  previewHostOrigin,
  previewVersionFromSearch,
  previewVersionParam,
} from './feedPreviewHost'
import {
  bookRegulationLabels,
  formatRegulationName,
  organizationRegulationMappings,
  sortRegulationsByLabel,
} from '../utils/regulationLabels'
import styles from './FeedPreviewPage.module.css'

const TRAILING_PERIODS_PATTERN = /\.+$/

const VIEWER_MODES = [
  { value: 'anonymous', label: 'Anonymous' },
  { value: 'identified', label: 'Identified' },
]

// URL value for an explicit "No offering" pick, so it is not mistaken for an
// absent parameter and replaced with the default offering.
const NO_OFFERING = 'none'

const NO_OFFERINGS = { offering_ids: [], default_offering_id: '', mappings: [] }

// Operator-facing copy says "regulation"; the parameter stays `offering`.
async function loadOfferings(orgSlug) {
  try {
    return await kambiApi.getOfferings(orgSlug)
  } catch (error) {
    if (error?.response?.status === 400) return NO_OFFERINGS
    throw error
  }
}

// Live and Release load a published brand bundle in the CDN preview host, so
// the preview shows what the brand's site serves; Studio build is the widget
// source compiled into this app (#551).
const VERSION_MODES = [
  { value: 'live', label: 'Live' },
  { value: 'release', label: 'Release' },
  { value: 'studio', label: 'Studio build' },
]
const LIVE_VERSION = { kind: 'live', releaseId: '' }
const STUDIO_VERSION = { kind: 'studio', releaseId: '' }

const PREVIEW_HOST_ORIGIN = previewHostOrigin()
const HOST_RESPONSE_TIMEOUT_MS = 15_000
const ACTIVITY_NOTICE_MS = 6000

const messageText = (value) => (typeof value === 'string' ? value : '')

function hostErrorNotice(message) {
  const detail = messageText(message).trim().replace(TRAILING_PERIODS_PATTERN, '')
  return `${detail || 'The preview host could not start'}. Showing the Studio build instead.`
}

function releaseStamp(kind, { releaseId, commit, feedEnv }) {
  const parts = [`${kind === 'live' ? 'Live release' : 'Release'} ${releaseId || 'unknown'}`]
  if (commit) parts.push(commit)
  // A staging CDN serves staging bundles; say so rather than imply production.
  if (feedEnv && feedEnv !== 'production') parts.push(feedEnv)
  return parts.join(' · ')
}

// Keyed by the release in the URL, so Back and Forward reset the draft.
function ReleaseIdForm({ initialReleaseId, onApply }) {
  const [draft, setDraft] = useState(initialReleaseId)
  const [error, setError] = useState('')
  const errorId = useId()

  return (
    <>
      <form
        className={styles.viewerForm}
        onSubmit={(event) => {
          event.preventDefault()
          const releaseId = draft.trim()
          if (!RELEASE_ID_PATTERN.test(releaseId)) {
            setError('Release ids use letters, digits, dots, dashes and underscores, like 20261002T010920Z-cef0ed61.')
            return
          }
          setError('')
          onApply(releaseId)
        }}
      >
        <Input
          type="text"
          aria-label="Release id"
          placeholder="20261002T010920Z-cef0ed61"
          value={draft}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => {
            setDraft(event.target.value)
            setError('')
          }}
        />
        <Button type="submit" variant="secondary" aria-label="Apply release" disabled={!draft.trim()}>Apply</Button>
      </form>
      {error ? <p id={errorId} className={styles.versionError} role="alert">{error}</p> : null}
    </>
  )
}

function RuntimeFeedPreview({ organization, apiUrl, surface, offering, punterId }) {
  const [runtimeReady, setRuntimeReady] = useState(
    () => typeof customElements !== 'undefined' && Boolean(customElements.get('wager-games-feed'))
  )
  const [runtimeFailed, setRuntimeFailed] = useState(false)

  useEffect(() => {
    if (runtimeReady) return undefined

    let active = true
    import('@feed-widget/feed-embed')
      .then(() => {
        if (active) setRuntimeReady(true)
      })
      .catch(() => {
        if (active) setRuntimeFailed(true)
      })

    return () => {
      active = false
    }
  }, [runtimeReady])

  if (runtimeFailed) {
    return <div className={styles.loadingFallback}>Feed runtime could not load.</div>
  }
  if (!runtimeReady) {
    return <div className={styles.loadingFallback}>Loading feed...</div>
  }

  return (
    <wager-games-feed
      class={styles.runtimeFeed}
      data-testid="feed-runtime"
      organization={organization}
      api-url={apiUrl}
      surface={surface}
      offering={offering || undefined}
      punter-id={punterId || undefined}
    />
  )
}

function FeedPreviewPage() {
  const { currentOrg } = useOrganization()
  const [searchParams, setSearchParams] = useSearchParams()
  // Manage posts links here with the surface the operator was already filtered to.
  const requestedSurface = searchParams.get('surface') || ''
  const requestedOffering = searchParams.get('offering') || ''
  const [refreshKey, setRefreshKey] = useState(0)
  const [runtimeKey, setRuntimeKey] = useState(0)
  const [viewerMode, setViewerMode] = useState('anonymous')
  const [punterId, setPunterId] = useState('')
  const [punterIdInput, setPunterIdInput] = useState('')
  const [releasePicking, setReleasePicking] = useState(false)
  const [hostStatus, setHostStatus] = useState(null)
  const [hostFailure, setHostFailure] = useState(null)
  const [activityNotice, setActivityNotice] = useState(null)
  const iframeRef = useRef(null)

  const orgSlug = currentOrg?.slug
  const apiUrl = getFeedPreviewApiUrl()

  // Only orgs with a published bundle have versions; the rest always preview
  // the Studio build and keep no version in the URL.
  const isWidgetBrand = WIDGET_BRANDS.includes(orgSlug)
  const requestedVersion = searchParams.get('version') || ''
  const version = isWidgetBrand ? previewVersionFromSearch(requestedVersion) || LIVE_VERSION : STUDIO_VERSION
  const versionKind = version.kind
  const canonicalVersion = isWidgetBrand ? previewVersionParam(version) : ''

  // Which release the brand's CDN `latest` alias serves right now (#552), read
  // from its manifest so the operator can see drift against this Studio build
  // whatever version the preview shows. null when it cannot be read. Refresh
  // refetches it: a release promoted while the page is open must restamp.
  const { data: publishedRelease = null, refetch: refetchPublishedRelease } = useQuery({
    queryKey: ['feed-preview-live-release', orgSlug],
    queryFn: ({ signal }) => fetchLatestWidgetRelease(orgSlug, { signal }),
    enabled: isWidgetBrand,
    retry: false,
    staleTime: 60_000,
  })
  const buildStamp = isWidgetBrand
    ? `${describeStudioBuild(STUDIO_BUILD)} · ${describePublishedRelease(publishedRelease)}`
    : ''
  const driftNotice = isWidgetBrand ? describeReleaseDrift(publishedRelease, STUDIO_BUILD) : null

  const {
    data: surfacesData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['feed-preview-surfaces', orgSlug],
    queryFn: () => surfacesApi.list(orgSlug),
    enabled: !!orgSlug,
  })

  const surfaces = useMemo(
    () => (surfacesData?.items || []).filter((surface) => surface.is_enabled),
    [surfacesData]
  )
  const defaultSurface = useMemo(
    () => surfaces.find((surface) => surface.include_by_default) || surfaces[0] || null,
    [surfaces]
  )
  const selectedSurface = useMemo(
    () => surfaces.find((surface) => surface.slug === requestedSurface) || defaultSurface,
    [defaultSurface, requestedSurface, surfaces]
  )
  const surfaceSlug = selectedSurface?.slug || ''

  // Every live embed carries an offering (the stable loaders always pass one),
  // and it decides both the live Kambi odds and which per-offering visibility
  // overrides apply. Previewing with one shows what a viewer on that offering
  // sees. A non-Kambi org answers 400 here, which simply means no offerings;
  // any other failure is surfaced, because an all-regulations preview without
  // live odds is easy to mistake for the live embed.
  const {
    data: offeringsData,
    isLoading: offeringsLoading,
    isError: offeringsError,
    refetch: refetchOfferings,
  } = useQuery({
    queryKey: ['feed-preview-offerings', orgSlug],
    queryFn: () => loadOfferings(orgSlug),
    enabled: !!orgSlug,
    retry: 1,
    staleTime: 60_000,
  })

  const offeringIds = useMemo(() => offeringsData?.offering_ids || [], [offeringsData])
  const offeringOptions = useMemo(() => {
    const regulations = new Map(organizationRegulationMappings(offeringsData)
      .filter((mapping) => mapping.regulation_name)
      .map((mapping) => [mapping.offering_id, mapping.regulation_name]))
    const entries = offeringIds.map((offeringId) => ({
      offeringId,
      regulation: formatRegulationName(regulations.get(offeringId)),
    }))
    const labels = bookRegulationLabels(entries)
    return sortRegulationsByLabel(entries.map(({ offeringId }) => ({
      value: offeringId,
      label: labels.get(offeringId),
    })))
  }, [offeringIds, offeringsData])
  const defaultOffering = useMemo(() => {
    const preferred = offeringsData?.default_offering_id
    return preferred && offeringIds.includes(preferred) ? preferred : offeringIds[0] || ''
  }, [offeringIds, offeringsData])
  const hasOfferings = offeringIds.length > 0
  // "All regulations" exists for the Studio build only: a published brand
  // bundle given no offering falls back to the host page's or its baked-in
  // one, so the feed would stay filtered while the control said otherwise.
  const allRegulationsAllowed = versionKind === 'studio'
  let offering = ''
  if (hasOfferings && (requestedOffering !== NO_OFFERING || !allRegulationsAllowed)) {
    offering = offeringIds.includes(requestedOffering) ? requestedOffering : defaultOffering
  }
  // Until offerings load (or when there are none) the URL is left alone.
  const canonicalOffering = hasOfferings ? offering || NO_OFFERING : ''

  useEffect(() => {
    const surfaceStale = surfaceSlug && surfaceSlug !== requestedSurface
    const offeringStale = canonicalOffering && canonicalOffering !== requestedOffering
    const versionStale = canonicalVersion && canonicalVersion !== requestedVersion
    if (!surfaceStale && !offeringStale && !versionStale) return
    // Canonicalize absent/retired links to the enabled default. Keeping the URL
    // authoritative also means browser Back and Forward change the preview.
    // One update for all three: setSearchParams reads the params of this
    // render, so two updates in the same commit would drop the first.
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      if (surfaceStale) next.set('surface', surfaceSlug)
      if (offeringStale) next.set('offering', canonicalOffering)
      if (versionStale) next.set('version', canonicalVersion)
      return next
    }, { replace: true })
  }, [
    canonicalOffering,
    canonicalVersion,
    requestedOffering,
    requestedSurface,
    requestedVersion,
    setSearchParams,
    surfaceSlug,
  ])

  // The frame waits for the offerings so it loads once with the default
  // offering instead of reloading when they arrive. Refresh is part of the
  // key: it reloads the frame and retries a bundle that failed before.
  const hostSrc = versionKind !== 'studio' && surfaceSlug && !offeringsLoading
    ? buildPreviewHostUrl({
      org: orgSlug,
      surface: surfaceSlug,
      offering,
      release: versionKind === 'release' ? version.releaseId : 'latest',
      // Empty outside development, where the host page picks its own Feed API.
      api: apiUrl,
    })
    : ''
  const hostKey = hostSrc ? `${hostSrc}#${refreshKey}` : ''
  // A failed bundle falls back to the Studio build without touching the URL,
  // so the link still says what the operator asked for.
  const hostFailed = Boolean(hostKey) && hostFailure?.key === hostKey
  const showHost = versionKind !== 'studio' && !hostFailed
  const hostReady = showHost && hostKey && hostStatus?.key === hostKey ? hostStatus : null
  const hostReadyKey = hostReady ? hostKey : ''

  useEffect(() => {
    if (!hostKey) return undefined

    // A host page that never loads (unpublished, blocked, a CDN error page)
    // sends nothing at all; give up on it like on a failed bundle.
    const silenceTimer = setTimeout(() => {
      setHostFailure({ key: hostKey, notice: 'The preview host did not respond; showing the Studio build instead.' })
    }, HOST_RESPONSE_TIMEOUT_MS)

    function handleHostMessage(event) {
      const message = parsePreviewHostMessage(event, PREVIEW_HOST_ORIGIN)
      // Only this frame: a message queued by a frame Refresh or a surface
      // change just removed carries the same origin but is not about this key.
      if (!message || event.source !== iframeRef.current?.contentWindow) return
      // The host reports its height before the bundle loads, so only the
      // outcome messages end the startup deadline.
      if (message.type === 'ready' || message.type === 'bundle-failed' || message.type === 'host-error') {
        clearTimeout(silenceTimer)
      }
      switch (message.type) {
        case 'ready':
          setHostStatus({
            key: hostKey,
            releaseId: messageText(message.releaseId),
            commit: messageText(message.commit),
            feedEnv: messageText(message.feedEnv),
          })
          break
        case 'bundle-failed':
          setHostFailure({
            key: hostKey,
            notice: `The published bundle for ${orgSlug} could not be loaded; showing the Studio build instead.`,
          })
          break
        case 'host-error':
          setHostFailure({ key: hostKey, notice: hostErrorNotice(message.message) })
          break
        case 'height':
          // Ignored: the feed's container is min-height 100vh, so a frame
          // sized from it could only grow. The frame fills the workspace.
          break
        default: {
          const text = hostActivityNotice(message)
          // A new id restarts the timer when the same betslip is sent twice.
          if (text) setActivityNotice((current) => ({ text, id: (current?.id || 0) + 1 }))
        }
      }
    }

    window.addEventListener('message', handleHostMessage)
    return () => {
      clearTimeout(silenceTimer)
      window.removeEventListener('message', handleHostMessage)
      // Leaving this frame (Studio build, another release, Refresh) forgets
      // what it reported: coming back mounts a new frame that must announce
      // itself again, and picking Live again retries a failed bundle.
      setHostStatus(null)
      setHostFailure(null)
    }
  }, [hostKey, orgSlug])

  // The host ignores messages until it is ready, and the viewer never goes in
  // the frame URL: send it on ready and whenever it changes.
  useEffect(() => {
    if (!hostReadyKey || !PREVIEW_HOST_ORIGIN) return
    iframeRef.current?.contentWindow?.postMessage({ type: 'set', punterId: punterId || null }, PREVIEW_HOST_ORIGIN)
  }, [hostReadyKey, punterId])

  useEffect(() => {
    if (!activityNotice) return undefined
    const timer = setTimeout(() => setActivityNotice(null), ACTIVITY_NOTICE_MS)
    return () => clearTimeout(timer)
  }, [activityNotice])

  const selectSurface = useCallback((nextSurface) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      next.set('surface', nextSurface)
      return next
    })
    setRefreshKey((key) => key + 1)
  }, [setSearchParams])

  const selectOffering = useCallback((nextOffering) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      next.set('offering', nextOffering || NO_OFFERING)
      return next
    })
    setRefreshKey((key) => key + 1)
  }, [setSearchParams])

  const refresh = useCallback(() => {
    setRefreshKey((key) => key + 1)
    if (isWidgetBrand) refetchPublishedRelease()
  }, [isWidgetBrand, refetchPublishedRelease])

  // A new viewer remounts only the embedded runtime. The preview host takes it
  // by message, and re-keying the frame would also retry a failed bundle.
  const remountRuntime = useCallback(() => setRuntimeKey((key) => key + 1), [])

  const applyViewerMode = useCallback((mode) => {
    setViewerMode(mode)
    if (mode !== 'anonymous' || !punterId) return
    setPunterId('')
    remountRuntime()
  }, [punterId, remountRuntime])

  const setVersionParam = useCallback((value) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      next.set('version', value)
      return next
    })
  }, [setSearchParams])

  // Release only opens the id form; the preview changes once an id is applied.
  const selectVersionMode = useCallback((mode) => {
    if (mode === 'release') {
      setReleasePicking(true)
      return
    }
    setReleasePicking(false)
    if (mode !== versionKind) setVersionParam(mode)
    // Picking the mode already in the URL after its host failed retries it.
    else if (hostFailed) setRefreshKey((key) => key + 1)
  }, [hostFailed, setVersionParam, versionKind])

  const applyRelease = useCallback((releaseId) => {
    setReleasePicking(false)
    setVersionParam(releaseId)
  }, [setVersionParam])

  const versionControlValue = releasePicking ? 'release' : versionKind
  let previewStamp = 'Studio build (embedded widget source)'
  if (showHost) previewStamp = hostReady ? releaseStamp(versionKind, hostReady) : `Loading ${orgSlug} bundle…`

  if (!orgSlug) {
    return (
      <PageLayout size="workspace">
        <PageHeader title="Feed preview" mode="workspace" />
        <StatePanel
          state="restricted"
          title="No organization selected"
          description="Pick an organization to preview its feed."
        />
      </PageLayout>
    )
  }

  return (
    <PageLayout size="workspace" className={styles.page}>
      <PageHeader
        title="Feed preview"
        context={currentOrg?.name || orgSlug}
        mode="compact"
      />

      {isLoading || offeringsLoading ? (
        <StatePanel
          state="loading"
          title="Loading feed surfaces…"
          description="Preparing the preview context."
        />
      ) : isError ? (
        <StatePanel
          state="error"
          title="Feed surfaces could not load"
          description="The preview was not changed."
          onRetry={refetch}
        />
      ) : offeringsError ? (
        <StatePanel
          state="error"
          title="Regulations could not load"
          description="Without a regulation the preview would show every regulation at once, which is not what a viewer sees."
          onRetry={refetchOfferings}
        />
      ) : !selectedSurface ? (
        <StatePanel
          state="empty"
          title="No enabled surfaces"
          description="Enable or create a surface before opening a live preview."
          action={<LinkButton href="/dashboard/feed/configuration" variant="secondary">Open Configuration</LinkButton>}
        />
      ) : (
        <>
          <PageToolbar
            label="Preview controls"
            className={styles.previewToolbar}
            filters={(
              <>
                <Field label="Surface" className={styles.surfaceField}>
                  <Select
                    value={surfaceSlug}
                    onChange={(event) => selectSurface(event.target.value)}
                    aria-label="Preview surface"
                  >
                    {surfaces.map((surface) => (
                      <option key={surface.id} value={surface.slug}>
                        {surface.name} · {labelForDisplayType(surface.display_type)}
                      </option>
                    ))}
                  </Select>
                </Field>
                {hasOfferings ? (
                  <Field label="Regulation" className={styles.offeringField}>
                    <Select
                      value={offering}
                      onChange={(event) => selectOffering(event.target.value)}
                      aria-label="Preview regulation"
                    >
                      {allRegulationsAllowed ? <option value="">All regulations</option> : null}
                      {offeringOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Select>
                  </Field>
                ) : null}
              </>
            )}
            viewControls={(
              <>
                {isWidgetBrand ? (
                  <Field label="Version" className={styles.versionField}>
                    <div className={styles.viewerControl}>
                      <SegmentedControl
                        items={VERSION_MODES}
                        value={versionControlValue}
                        onValueChange={selectVersionMode}
                        label="Preview version"
                      />
                      {versionControlValue === 'release' ? (
                        <ReleaseIdForm
                          key={version.releaseId}
                          initialReleaseId={version.releaseId}
                          onApply={applyRelease}
                        />
                      ) : null}
                    </div>
                  </Field>
                ) : null}
                <Field label="Viewer" className={styles.viewerField}>
                  <div className={styles.viewerControl}>
                    <SegmentedControl
                      items={VIEWER_MODES}
                      value={viewerMode}
                      onValueChange={applyViewerMode}
                      label="Viewer mode"
                    />
                    {viewerMode === 'identified' ? (
                      <form
                        className={styles.viewerForm}
                        onSubmit={(event) => {
                          event.preventDefault()
                          setPunterId(punterIdInput)
                          remountRuntime()
                        }}
                      >
                        <Input
                          type="text"
                          aria-label="Viewer identifier"
                          placeholder="Viewer identifier"
                          value={punterIdInput}
                          onChange={(event) => setPunterIdInput(event.target.value)}
                        />
                        <Button type="submit" variant="secondary" disabled={!punterIdInput.trim()}>Apply</Button>
                      </form>
                    ) : null}
                  </div>
                </Field>
              </>
            )}
            actions={(
              <Button variant="secondary" onClick={refresh}>
                <RotateCw size={16} aria-hidden="true" />
                Refresh
              </Button>
            )}
          />

          <p className={styles.previewStamp} data-testid="feed-preview-stamp">{previewStamp}</p>
          {buildStamp ? (
            <p className={styles.previewStamp} data-testid="feed-preview-build">{buildStamp}</p>
          ) : null}
          {driftNotice ? (
            <p className={styles.previewNotice} role="status" data-testid="feed-preview-drift">{driftNotice}</p>
          ) : null}
          {hostFailed ? (
            <p className={styles.previewNotice} role="status">{hostFailure.notice}</p>
          ) : null}
          {activityNotice ? (
            <p className={`${styles.previewNotice} ${styles.previewNoticeInfo}`} role="status">{activityNotice.text}</p>
          ) : null}

          <section className={styles.previewWorkspace} aria-label={`${selectedSurface.name} feed preview`}>
            <div className={`${styles.previewArea} ${showHost ? styles.previewAreaHost : ''}`.trim()} key={refreshKey}>
              {!showHost ? (
                <RuntimeFeedPreview
                  key={runtimeKey}
                  organization={orgSlug}
                  apiUrl={apiUrl}
                  surface={surfaceSlug}
                  offering={offering || undefined}
                  punterId={punterId || undefined}
                />
              ) : hostSrc ? (
                <iframe
                  key={hostKey}
                  ref={iframeRef}
                  title="Feed preview"
                  data-testid="feed-preview-host"
                  src={hostSrc}
                  sandbox="allow-scripts allow-same-origin allow-popups"
                  allow="autoplay"
                  // The host answers only the origin it reads from the referrer.
                  referrerPolicy="strict-origin-when-cross-origin"
                  className={styles.previewHostFrame}
                />
              ) : (
                <div className={styles.loadingFallback}>Loading feed...</div>
              )}
            </div>
          </section>
        </>
      )}
    </PageLayout>
  )
}

export default FeedPreviewPage
